import { useState, useEffect } from "react"
import { LayoutDashboard, Lightbulb, MessageCircleQuestion, Trophy, BookOpen, LogOut, Bot, Video, Moon, Sun, Shield, Settings, Users, BarChart3, Target, MessageCircle, PieChart } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTheme } from "next-themes"
import { AnimatedSidebar, SidebarBody, SidebarLink, useSidebar } from "@/components/ui/animated-sidebar"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { motion } from "framer-motion"
import nataliaBeautyAvatar from "@/assets/natalia-beauty-avatar.jpg"
import { useUserRole } from "@/hooks/use-user-role"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"
import { NotificationCenter } from "@/components/NotificationCenter"

const menuItems = [
  { label: "Painel", href: "/dashboard", icon: <LayoutDashboard className="h-4 w-4 flex-shrink-0" /> },
  { label: "Hub de IA", href: "/assistentes", icon: <Bot className="h-4 w-4 flex-shrink-0" /> },
  { label: "Lives Gravadas", href: "/lives", icon: <Video className="h-4 w-4 flex-shrink-0" /> },
  { label: "Transformações", href: "/cases", icon: <Trophy className="h-4 w-4 flex-shrink-0" /> },
  { label: "Meus Cursos", href: "/cursos", icon: <BookOpen className="h-4 w-4 flex-shrink-0" /> },
  { label: "Guia da Comunidade", href: "/regras", icon: <BookOpen className="h-4 w-4 flex-shrink-0" /> },
  { label: "Tire Dúvidas", href: "/duvidas", icon: <MessageCircleQuestion className="h-4 w-4 flex-shrink-0" /> },
  { label: "Meus Relatórios", href: "/meus-relatorios", icon: <BarChart3 className="h-4 w-4 flex-shrink-0 text-purple-500" /> },
  { label: "Desafios", href: "/desafios", icon: <Target className="h-4 w-4 flex-shrink-0 text-orange-500" /> },
  { label: "Mensagens", href: "/mensagens", icon: <MessageCircle className="h-4 w-4 flex-shrink-0 text-blue-500" /> },
]

const adminItems = [
  { label: "Dashboard", href: "/admin/dashboard", icon: <PieChart className="h-4 w-4 flex-shrink-0 text-purple-500" /> },
  { label: "Membros", href: "/admin/users", icon: <Users className="h-4 w-4 flex-shrink-0" /> },
  { label: "Relatórios", href: "/admin/relatorios", icon: <BarChart3 className="h-4 w-4 flex-shrink-0 text-emerald-500" /> },
  { label: "Cursos", href: "/admin/courses", icon: <BookOpen className="h-4 w-4 flex-shrink-0" /> },
  { label: "Lives", href: "/admin/lives", icon: <Video className="h-4 w-4 flex-shrink-0" /> },
  { label: "Moderação", href: "/moderation", icon: <Shield className="h-4 w-4 flex-shrink-0" /> },
  { label: "Gamificação", href: "/gamification-admin", icon: <Settings className="h-4 w-4 flex-shrink-0" /> },
]

export function AppSidebar() {
  const [open, setOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const { permissions } = useUserRole()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<any>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!user) return

    // Fetch initial profile
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (data) setProfile(data)
    }

    fetchProfile()

    // Subscribe to profile changes
    const channel = supabase
      .channel('sidebar_profile_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          setProfile(payload.new)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  const currentTheme = mounted ? theme : "light"

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const userInitials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
    : 'NB'

  const userName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Usuário'
    : 'Natalia Beauty'

  return (
    <AnimatedSidebar open={open} setOpen={setOpen}>
      <SidebarBody className="justify-between gap-10">
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
          {/* Logo */}
          <Logo open={open} />

          {/* Menu Items */}
          <div className="mt-8 flex flex-col gap-2">
            {menuItems.map((link, idx) => (
              <SidebarLink key={idx} link={link} />
            ))}
          </div>

          {/* Admin Section - Only visible to admins/moderators */}
          {(permissions.isAdmin || permissions.isModerator) && (
            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <motion.h3
                animate={{
                  display: open ? "block" : "none",
                  opacity: open ? 1 : 0,
                }}
                className="px-2 text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2"
              >
                Administração
              </motion.h3>
              <div className="flex flex-col gap-2">
                {adminItems.map((link, idx) => (
                  <SidebarLink key={idx} link={link} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col gap-2">
          {/* Theme Toggle */}
          {mounted && (
            <button
              onClick={() => setTheme(currentTheme === "dark" ? "light" : "dark")}
              className="flex items-center justify-start gap-2 group/sidebar py-2 px-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors"
            >
              {currentTheme === "dark" ? (
                <Sun className="h-4 w-4 flex-shrink-0 text-neutral-700 dark:text-neutral-200" />
              ) : (
                <Moon className="h-4 w-4 flex-shrink-0 text-neutral-700 dark:text-neutral-200" />
              )}
              <motion.span
                animate={{
                  display: open ? "inline-block" : "none",
                  opacity: open ? 1 : 0,
                }}
                className="text-sm whitespace-pre inline-block !p-0 !m-0 text-neutral-700 dark:text-neutral-200"
              >
                {currentTheme === "dark" ? "Modo Claro" : "Modo Escuro"}
              </motion.span>
            </button>
          )}

          {/* Profile Link & Notifications */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 overflow-hidden">
              <SidebarLink
                link={{
                  label: userName,
                  href: "/perfil",
                  icon: (
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarImage src={profile?.avatar_url || nataliaBeautyAvatar} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-[10px]">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  ),
                }}
              />
            </div>
            {open && <NotificationCenter />}
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center justify-start gap-2 group/sidebar py-2 px-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors"
          >
            <LogOut className="h-4 w-4 flex-shrink-0 text-neutral-700 dark:text-neutral-200" />
            <motion.span
              animate={{
                display: open ? "inline-block" : "none",
                opacity: open ? 1 : 0,
              }}
              className="text-sm whitespace-pre inline-block !p-0 !m-0 text-neutral-700 dark:text-neutral-200"
            >
              Sair
            </motion.span>
          </button>
        </div>
      </SidebarBody>
    </AnimatedSidebar>
  )
}

const Logo = ({ open }: { open: boolean }) => {
  return (
    <div className="font-normal flex space-x-2 items-center text-sm py-1 relative z-20">
      <div className="w-8 h-8 rounded-xl bg-gradient-primary flex items-center justify-center font-heading font-bold text-primary-foreground text-base flex-shrink-0">
        NB
      </div>
      <motion.div
        animate={{
          display: open ? "block" : "none",
          opacity: open ? 1 : 0,
        }}
        className="flex-1"
      >
        <h2 className="font-heading font-bold text-base text-neutral-700 dark:text-neutral-200">Natalia Beauty</h2>
        <p className="text-[10px] text-neutral-500 dark:text-neutral-400">Beauty University</p>
      </motion.div>
    </div>
  )
}